"use client"

import { Leaf, Sprout } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { useNuisible } from "../../hooks/api/nuisibles";
import { formatDate } from "../../lib/utils";

interface NuisibleDetailProps {
  nuisibleId: number;
  onBack: () => void;
  onEdit: (id: number) => void;
}

export function NuisibleDetail({
  nuisibleId,
  onBack,
  onEdit,
}: NuisibleDetailProps) {

  const { data: nuisibleResponse, isLoading, error } = useNuisible(nuisibleId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex space-x-2">
            <Skeleton className="h-10 w-20" />
            <Skeleton className="h-10 w-20" />
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <Skeleton className="h-6 w-40" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-red-600">Erreur lors du chargement du nuisible</p>
            <Button variant="outline" onClick={onBack} className="mt-2">
              Retour à la liste
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!nuisibleResponse?.data) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-gray-600">Nuisible non trouvé</p>
            <Button variant="outline" onClick={onBack} className="mt-2">
              Retour à la liste
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const nuisibleData = nuisibleResponse.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {nuisibleData.name || "Nuisible sans nom"}
          </h2>
          <p className="text-sm text-gray-500">
            Détails du nuisible
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={onBack}>
            Retour
          </Button>
          <Button onClick={() => onEdit(nuisibleData.id)}>
            Modifier
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* General Information */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Leaf className="h-5 w-5 text-green-600" /> Informations générales
          </h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-gray-700">
            <div>
              <dt className="font-medium text-gray-500">ID:</dt>
              <dd className="text-lg font-semibold">{nuisibleData.id}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Nom:</dt>
              <dd className="text-lg font-semibold">{nuisibleData.name || "Non spécifié"}</dd>
            </div>
          </dl>
        </div>

        <Separator />

        {/* Dates */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Sprout className="h-5 w-5 text-green-600" /> Informations de gestion
          </h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-gray-700">
            <div>
              <dt className="font-medium text-gray-500">Date de création:</dt>
              <dd className="text-lg">
                {nuisibleData.created_at
                  ? formatDate(new Date(nuisibleData.created_at))
                  : "Non spécifiée"
                }
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Dernière modification:</dt>
              <dd className="text-lg">
                {nuisibleData.updated_at
                  ? formatDate(new Date(nuisibleData.updated_at))
                  : "Non spécifiée"
                }
              </dd>
            </div>
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}
